import React, { useState, useEffect, useRef } from 'react';
import { Modal, Table, Button, Select, message, DatePicker, Input, Tag } from 'antd';
import { useQuery } from '@apollo/client';
import { GET_PICKUP_APPOINTMENT_LISTING } from '../../appointment/graphql/queries/getPickupAppointmentListing';
import type { 
    PickupAppointmentType, 
    PickupAppointmentListingResponse 
} from '../../appointment/graphql/queries/getPickupAppointmentListing';
import type { ColumnsType } from 'antd/es/table';
import { packageUnitFilterOptions, statusOptions } from '../../appointment/types/PickupAppointmentType';
import { useWarehouseContext } from "../../../../contexts/WarehouseContext";
import { CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import TablePaginationBar from '../../../../components/TablePaginationBar';

interface AddAppointmentModalProps {
    visible: boolean;
    onClose: () => void;
    onConfirm: (selectedAppointments: string[]) => void;
    currentTaskDate?: string;
}

const AddAppointmentModal: React.FC<AddAppointmentModalProps> = ({
    visible,
    onClose,
    onConfirm,
    currentTaskDate
}) => {
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<PickupAppointmentType[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [zipcode, setZipcode] = useState<string | undefined>(undefined);
    const [unitType, setUnitType] = useState<string | undefined>(undefined);
    const [appointmentNumber, setAppointmentNumber] = useState<string>('');
    const [appointmentStatus, setAppointmentStatus] = useState<string | undefined>('CREATED');
    const [pickupSiteId, setPickupSiteId] = useState<string>('');
    const [addressFilter, setAddressFilter] = useState<string>('');
    const [contactPhoneFilter, setContactPhoneFilter] = useState<string>('');
    const firstLoadRef = useRef(true);
    const { currentWarehouse } = useWarehouseContext();
    const warehouseId = currentWarehouse?.id;
    const { t } = useTranslation();
    // 使用传入的 currentTaskDate 或当前日期
    const pickupDate = currentTaskDate || dayjs().format('YYYY-MM-DD');
    const pickupDateDayjs = dayjs(pickupDate);

    // 支持多个预约单号，使用多种分隔符：逗号、空格、换行、分号、制表符
    const appointmentNumberTrimmed = appointmentNumber.trim();
    const appointmentNumberFilters = appointmentNumberTrimmed
        ? appointmentNumberTrimmed
            .split(/[,\s\n\r\t;]+/)
            .map(s => s.trim())
            .filter(s => s.length > 0)
        : undefined;
    const pickupSiteIdTrimmed = pickupSiteId.trim();
    const pickupSiteIdNumber = pickupSiteIdTrimmed !== '' ? Number(pickupSiteIdTrimmed) : undefined;
    const cooperationWarehouseIdFilter = pickupSiteIdNumber !== undefined && !Number.isNaN(pickupSiteIdNumber)
        ? pickupSiteIdNumber
        : undefined;
    const addressTrimmed = addressFilter.trim();
    const contactPhoneTrimmed = contactPhoneFilter.trim();

    // 查询参数
    const variables = {
        filters: {
            expectedPickupDate: pickupDate,
            status: appointmentStatus,
            zipcode: zipcode,
            expectedParcelUnit: unitType,
            warehouseId: warehouseId ? Number(warehouseId) : undefined,
            appointmentNumberList: appointmentNumberFilters,
            cooperationWarehouseId: cooperationWarehouseIdFilter,
            address: addressTrimmed || undefined,
            contactPhone: contactPhoneTrimmed || undefined
        },
        pageRequest: {
            page: currentPage,
            size: pageSize,
            sortBy: "pickupDate",
            sortDirection: "DESC"
        }
    };

    // 执行查询
    const { data, loading, refetch } = useQuery<PickupAppointmentListingResponse>(
        GET_PICKUP_APPOINTMENT_LISTING,
        {
            variables,
            fetchPolicy: 'network-only',
            skip: !visible || !warehouseId // 只有在模态框可见且有 warehouseId 时才执行查询
        }
    );

    // 当模态框打开时重新获取数据
    useEffect(() => {
        if (visible) {
            // 重新获取数据
            refetch(variables);

            // 重置筛选条件
            if (!firstLoadRef.current) {
                setZipcode(undefined);
                setUnitType(undefined);
                setAppointmentNumber('');
                setAppointmentStatus('CREATED');
                setPickupSiteId('');
                setAddressFilter('');
                setContactPhoneFilter('');
                setCurrentPage(1);
            }
            firstLoadRef.current = false;
        }
    }, [visible]);

    // 当筛选条件变化时重新查询
    useEffect(() => {
        if (visible && !firstLoadRef.current) {
            refetch(variables);
        }
    }, [currentPage, pageSize, zipcode, unitType, appointmentNumber, appointmentStatus, pickupSiteId, addressFilter, contactPhoneFilter]);

    // 重置选择
    useEffect(() => {
        if (!visible) {
            setSelectedRowKeys([]);
            setSelectedRows([]);
        }
    }, [visible]);

    const appointments = data?.getPickupAppointmentListing?.content || [];
    const totalElements = data?.getPickupAppointmentListing?.totalElements || 0;

    // 获取禁用原因
    const getDisabledReason = (record: PickupAppointmentType): string => {
        if (record.status !== 'CREATED') {
            return `Status: ${record.status}`;
        }
        if (record.taskNumber) {
            return `Already in task: ${record.taskNumber}`;
        }
        return '';
    };

    const columns: ColumnsType<PickupAppointmentType> = [
        {
            title: t('pickup.pickupTask.addAppointmentModal.title') || '',
            dataIndex: 'appointmentNumber',
            key: 'appointmentNumber',
            width: 180,
            render: (text) => <span className="text-purple-600">{text}</span>
        },
        {
            title: t('pickup.pickupTask.addAppointmentModal.customerName') || '',
            dataIndex: 'channelName',
            key: 'channelName',
            width: 150,
        },
        {
            title: t('pickup.pickupTask.addAppointmentModal.pickupSite') || '',
            dataIndex: 'cooperationWarehouseId',
            key: 'cooperationWarehouseId',
            width: 150,
        },
        {
            title: t('pickup.pickupTask.addAppointmentModal.phoneNumber') || '',
            dataIndex: 'contactPhone',
            key: 'contactPhone',
            width: 150,
        },
        {
            title: t('pickup.pickupTask.addAppointmentModal.address') || '',
            key: 'address',
            width: 200,
            render: (_, record) => (
                <div className="whitespace-pre-line">
                    {record.addressLine1}
                    {record.addressLine2 && <div>{record.addressLine2}</div>}
                </div>
            ),
        },
        {
            title: t('pickup.pickupTask.addAppointmentModal.zipcode') || '',
            dataIndex: 'zipcode',
            key: 'zipcode',
            width: 100,
        },
        {
            title: t('pickup.pickupTask.addAppointmentModal.expectedTime') || '',
            key: 'expectedPickupTime',
            width: 150,
            render: (_, record) => (
                <div>{record.expectedPickupTimeStart}-{record.expectedPickupTimeEnd}</div>
            ),
        },
        {
            title: t('pickup.pickupTask.addAppointmentModal.timezone') || '',
            dataIndex: 'timezone',
            key: 'timezone',
            width: 100,
        },
        {
            title: t('pickup.pickupTask.addAppointmentModal.expectedCount') || '',
            dataIndex: 'expectedParcelCount',
            key: 'expectedParcelCount',
            width: 150,
            align: 'left',
        },
        {
            title: t('pickup.pickupTask.addAppointmentModal.expectedUnit') || '',
            dataIndex: 'expectedParcelUnit',
            key: 'expectedParcelUnit',
            width: 150,
            align: 'left',
            render: (unit) => packageUnitFilterOptions.find(option => option.value === unit)?.label || unit || '--',
        },
        {
            title: t('pickup.pickupTask.addAppointmentModal.status') || '',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            render: (status, record) => {
                const reason = getDisabledReason(record);
                if (reason) {
                    return (
                        <div>
                            <Tag color={status === 'CREATED' ? 'orange' : 'red'}>{status}</Tag>
                            {reason !== `Status: ${status}` && (
                                <div className="text-xs text-gray-500 mt-1">{reason}</div>
                            )}
                        </div>
                    );
                }
                return <Tag color="green">{status}</Tag>;
            }
        },
    ];

    // 处理表格选择变化
    const handleSelectChange = (newSelectedRowKeys: React.Key[], newSelectedRows: PickupAppointmentType[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
        setSelectedRows(newSelectedRows);
    };

    // 处理添加预约
    const handleOk = () => {
        if (selectedRows.length === 0) {
            message.warning(t('pickup.pickupTask.addAppointmentModal.pleaseSelectAtLeastOneAppointment') || '');
            return;
        }
        // 转换为字符串数组以保持接口兼容性
        const selectedIds = selectedRows.map(row => row.id.toString());
        onConfirm(selectedIds);
        onClose();
    };

    const handleCancel = () => {
        onClose();
    };

    const handlePageChange = (page: number, size?: number) => {
        setCurrentPage(page);
        if (size) setPageSize(size);
    };

    // 单位选项
    const unitOptions = [
        { label: 'All', value: undefined },
        ...packageUnitFilterOptions
    ];

    return (
        <Modal
            title={
                <div className="flex items-center">
                    <div className="h-6 w-1 bg-purple-600 mr-3"></div>
                    <span className="text-xl font-medium">{t('pickup.pickupTask.addAppointmentModal.title') || ''}</span>
                </div>
            }
            open={visible}
            onCancel={handleCancel}
            width={1600}
            footer={null}
            closeIcon={<CloseOutlined />}
            destroyOnClose={true}
        >
            <div className="space-y-4">
                {/* 筛选条件 */}
                <div className="flex items-center space-x-4 mb-4">
                    <div className="flex items-center">
                        <span className="mr-2">{t('pickup.pickupTask.addAppointmentModal.pickupDate') || ''}</span>
                        <DatePicker
                            value={pickupDateDayjs}
                            disabled
                            style={{ width: 120 }}
                        />
                    </div>
                    <div className="flex items-center">
                        <span className="mr-2">{t('pickup.pickupTask.addAppointmentModal.appointmentNumber') || ''}</span>
                        <Input
                            style={{ width: 180 }}
                            value={appointmentNumber}
                            onChange={(event) => setAppointmentNumber(event.target.value)}
                            placeholder={t('pickup.pickupTask.addAppointmentModal.appointmentNumber') || '支持多个，用逗号或空格分隔'}
                        />
                    </div>
                    <div className="flex items-center">
                        <span className="mr-2">
                            {(t('pickup.pickupTask.addAppointmentModal.pickupSite') || 'Pickup Site')}
                        </span>
                        <Input
                            style={{ width: 100 }}
                            value={pickupSiteId}
                            onChange={(event) => setPickupSiteId(event.target.value)}
                            placeholder={t('pickup.pickupTask.addAppointmentModal.pickupSite') || ''}
                        />
                    </div>
                    <div className="flex items-center">
                        <span className="mr-2">{t('pickup.pickupTask.addAppointmentModal.status') || ''}</span>
                        <Select
                            style={{ width: 100 }}
                            options={statusOptions}
                            value={appointmentStatus}
                            onChange={(value) => setAppointmentStatus(value)}
                            placeholder={t('components.input.all') || ''}
                            allowClear
                        />
                    </div>
                </div>

                <div className="flex items-center space-x-4 mb-4">
                    <div className="flex items-center">
                        <span className="mr-2">{t('pickup.pickupTask.addAppointmentModal.zipcode') || ''}</span>
                        <Select
                            style={{ width: 120 }}
                            value={zipcode}
                            onChange={(value) => setZipcode(value)}
                            placeholder=""
                            allowClear
                            showSearch
                            options={
                                data?.getPickupAppointmentListing?.content
                                    ? Array.from(new Set(data.getPickupAppointmentListing.content.map(item => item.zipcode)))
                                        .map(code => ({ label: code, value: code }))
                                    : []
                            }
                        />
                    </div>
                    <div className="flex items-center">
                        <span className="mr-2">{t('pickup.pickupTask.addAppointmentModal.address') || ''}</span>
                        <Input
                            style={{ width: 240 }}
                            value={addressFilter}
                            onChange={(event) => setAddressFilter(event.target.value)}
                            placeholder={t('pickup.pickupTask.addAppointmentModal.address') || ''}
                        />
                    </div>
                    <div className="flex items-center">
                        <span className="mr-2">{t('pickup.pickupTask.addAppointmentModal.phoneNumber') || ''}</span>
                        <Input
                            style={{ width: 120 }}
                            value={contactPhoneFilter}
                            onChange={(event) => setContactPhoneFilter(event.target.value)}
                            placeholder={t('pickup.pickupTask.addAppointmentModal.phoneNumber') || ''}
                        />
                    </div>
                    <div className="flex items-center">
                        <span className="mr-2">{t('pickup.pickupTask.addAppointmentModal.expectedUnit') || ''}</span>
                        <Select
                            style={{ width: 120 }}
                            options={unitOptions}
                            value={unitType}
                            onChange={(value) => setUnitType(value)}
                            placeholder={t('components.input.all') || ''}
                            allowClear
                        />
                    </div>
                </div>

                {/* 表格 */}
                <Table
                    rowSelection={{
                        type: 'checkbox',
                        selectedRowKeys,
                        onChange: handleSelectChange,
                        getCheckboxProps: (record) => ({
                            disabled: record.status !== 'CREATED'
                        })
                    }}
                    columns={columns}
                    dataSource={appointments}
                    rowKey="id"
                    loading={loading}
                    pagination={false}
                    size="small"
                    className="mb-4"
                    rowClassName={(record) => {
                        if (record.status !== 'CREATED') {
                            return 'text-gray-400 bg-gray-50';
                        }
                        return '';
                    }}
                />

                {/* 分页 */}
                <TablePaginationBar
                    total={totalElements}
                    current={currentPage}
                    pageSize={pageSize}
                    onChange={handlePageChange}
                    showQuickJumper={false}
                />

                {/* 底部按钮 */}
                <div className="flex justify-end mt-4 space-x-2">
                    <Button
                        onClick={handleCancel}
                        className="text-purple-600"
                    >
                        {t('components.button.cancel') || ''}
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleOk}
                        className="bg-purple-600 hover:bg-purple-700"
                        disabled={selectedRows.length === 0}
                    >
                        {t('pickup.pickupTask.addAppointmentModal.addSelected', {count: selectedRows.length}) || ''}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default AddAppointmentModal; 